import { CellStatus } from '@fluentsms/agentnet-web-components';
import { deepPurple, green } from '@material-ui/core/colors';
import { GridCellValue, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { Firm } from 'api/profile/types';
import addHours from 'date-fns/addHours';
import addMinutes from 'date-fns/addMinutes';
import addSeconds from 'date-fns/addSeconds';
import formatDate from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import subYears from 'date-fns/subYears';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { DefaultNewFile } from 'models/file-model';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import palette from 'ui-kit/theme/palette';
import { dateTooltipValue } from 'utilities/utilities';
import FileStatus from '../../../../ui-kit/icons/FileStatus';
import { dateRangeFilters } from '../../../constants';
import './FilesList.scss';
type DataType = { accountNumber?: string; dataTestId?: string };
export const FileNumber = ({
  id,
  fileNumber,
  data,
  firmId,
  hideNavigation,
}: {
  id: any;
  fileNumber: GridCellValue;
  data?: DataType | GridRenderCellParams;
  firmId?: string;
  hideNavigation?: boolean;
}): JSX.Element => {
  const history = useHistory();
  const details = data as DataType;
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, setUserFirm, profile } = profileCtx;
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { setTabValue, setFileData, setSessionFileId } = fileDataCtx;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);

  function handleClick() {
    setSessionFileId?.(id);
    setFileData?.(DefaultNewFile);
    if (details?.accountNumber) window.sessionStorage.setItem('accountNumber', details.accountNumber);
    if (firmId && firmId !== userFirm?.firmId) {
      if (!profile?.firms) return undefined;
      const fId: Firm | undefined = profile.firms.find((firm: Firm) => firm.firmId === firmId);

      if (fId) {
        window.localStorage.setItem('userFirm', JSON.stringify(fId));
        setUserFirm && setUserFirm(fId);
      }
    }

    // Clear FinCEN validation error cache when clicking on a file
    if (fincenReportCtx) {
      fincenReportCtx.clearReportingPartyValidationErrors?.();
      fincenReportCtx.clearPropertyAddressValidationErrors?.();
      fincenReportCtx.clearTransfereeValidationErrors?.();
      fincenReportCtx.clearTransferorValidationErrors?.();
      fincenReportCtx.clearPaymentValidationErrors?.();
      fincenReportCtx.clearCertifierValidationErrors?.();
      fincenReportCtx.setGenerateButtonClicked?.(false);
      fincenReportCtx.setAutoValidationTriggered?.(false);

      // Clear the entire FinCEN report to ensure fresh state
      fincenReportCtx.clearFincenReport?.();
    }

    setTabValue && setTabValue('0');
    history.push(`/files/${id}/file-info`);
  }
  if (hideNavigation) {
    return <div data-testid={`${details?.dataTestId ?? ''}${fileNumber}`}>{fileNumber}</div>;
  }

  return (
    <div className="fileNumber" onClick={handleClick} data-testid={`${details?.dataTestId ?? ''}${fileNumber}`}>
      {fileNumber}
    </div>
  );
};

export const FormatDate = ({ value }: { value: string }): string => {
  if (!value) return '';
  const dateParsed = Date.parse(value);
  const valueFormatted = formatDate(dateParsed, 'MM/dd/yyyy');
  return valueFormatted;
};

export const getDateRange = (
  type: string,
  filterModelDateFrom: string,
  filterModelDateTo: string,
  years = 10,
): { dateFrom: Date; dateTo: Date; dateFromFormatISO: string; dateToFormatISO: string } => {
  const getStartOfDay = (date: Date): Date => new Date(formatDate(date, 'MM/dd/yyyy'));
  const getEndOfDay = (date: Date): Date => addSeconds(addMinutes(addHours(date, 23), 59), 59);

  const parseDateFrom = new Date(filterModelDateFrom);
  const parseDateTo = type !== dateRangeFilters.equal ? new Date(filterModelDateTo) : new Date(filterModelDateFrom);
  const parseDateToday = getStartOfDay(new Date());
  const dateFrom = type !== dateRangeFilters.lessThan ? parseDateFrom : subYears(parseDateFrom, years);
  const dateTo =
    type === dateRangeFilters.lessThan
      ? getEndOfDay(parseDateFrom)
      : type === dateRangeFilters.greaterThan
      ? getEndOfDay(parseDateToday)
      : getEndOfDay(parseDateTo);

  return { dateFrom, dateTo, dateFromFormatISO: formatISO(dateFrom), dateToFormatISO: formatISO(dateTo) };
};

export const StatusRenderer = (params: any): React.ReactElement => {
  const { value } = params;
  const lowerCasedValue = value?.toLowerCase();
  let statusDotClassName, statusClassName;
  if (lowerCasedValue === 'open' || lowerCasedValue === 'reopened') {
    statusDotClassName = 'green-dot';
    statusClassName = 'open-status';
  } else if (lowerCasedValue?.includes('in process')) {
    statusDotClassName = 'pending-dot';
    statusClassName = 'pending-status';
  } else if (lowerCasedValue === 'cancelled') {
    statusDotClassName = 'grey-dot';
    statusClassName = 'cancelled-status';
  } else if (lowerCasedValue === 'closed') {
    statusDotClassName = 'blue-dot';
    statusClassName = 'closed';
  } else if (lowerCasedValue === 'processed') {
    statusDotClassName = 'green-dot';
    statusClassName = 'open-status';
  } else if (lowerCasedValue === 'processing') {
    statusDotClassName = 'pending-dot';
    statusClassName = 'pending-status';
  } else if (lowerCasedValue === 'pending') {
    statusDotClassName = 'pending-dot';
    statusClassName = 'pending-status';
  } else {
    statusDotClassName = 'dot';
    statusClassName = 'error-status';
  }

  return FileStatus({
    value: params?.value,
    statusDotClassName: statusDotClassName,
    statusClassName: statusClassName,
  });
};

type TextFilterModel = { filter?: string } | undefined;

export function getTextFilters(model: Readonly<Record<string, unknown>>, headerSearch: string) {
  const m = model as Readonly<Record<string, TextFilterModel>>;
  return {
    fileNumber: m.fileNumber?.filter ?? '',
    address: m.address?.filter ?? '',
    buyer: m.buyer?.filter ?? '',
    searchText: headerSearch ?? '',
  };
}

type FileStatusType =
  | 'Cancelled'
  | 'Closed'
  | 'In Process'
  | 'Open'
  | 'Pending'
  | 'Processing'
  | 'Opened in Error'
  | 'Processed'
  | 'Reopened';

export const StatusCellRenderer = (params: ICellRendererParams) => {
  const customColors: Record<FileStatusType, string> = {
    Cancelled: palette.secondary.main,
    Closed: deepPurple[600],
    'In Process': palette.warning.dark,
    Open: green[600],
    Pending: palette.warning.dark,
    Processing: palette.warning.dark,
    'Opened in Error': palette.error.main,
    Processed: green[600],
    Reopened: green[600],
  };
  return <CellStatus colorsByStatus={customColors} status={params.value} />;
};

export const defaultConfig = {
  flex: 1,
  headerClassName: 'column-headers',
};

const buildHeaderTemplate = (sortTestId: string, filterIconTestId?: string) => `
  <div class="ag-cell-label-container" role="presentation">
    <div ref="eLabel" class="ag-header-cell-label" role="presentation">
      <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
      <span ref="eFilter" ${
        filterIconTestId ? `data-testid="${filterIconTestId}"` : ''
      } class="ag-header-icon ag-header-label-icon ag-filter-icon"></span>
      <span ref="eSortOrder" data-testid="${sortTestId}" class="ag-header-icon ag-header-label-icon ag-sort-order ag-hidden"></span>
      <span ref="eSortAsc" data-testid="${sortTestId}" class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon ag-hidden"></span>
      <span ref="eSortDesc" data-testid="${sortTestId}" class="ag-header-icon ag-header-label-icon ag-sort-descending-icon ag-hidden"></span>
      <span ref="eSortNone" data-testid="${sortTestId}" class="ag-header-icon ag-header-label-icon ag-sort-none-icon ag-hidden"></span>
    </div>
  </div>`;

const getStartOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const getFileListColDefs = (): ColDef[] => [
  {
    field: 'status',
    headerName: 'Status',
    tooltipField: 'status',
    cellRenderer: StatusCellRenderer,
    width: 120,
    minWidth: 120,
    filter: true,
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListStatusSort', 'FileListStatusIcon'),
    },
  },
  {
    field: 'fileNumber',
    headerName: 'File Number',
    tooltipField: 'fileNumber',
    width: 220,
    minWidth: 220,
    floatingFilterComponent: 'customFloatingFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
    cellRenderer: 'fileNumberRenderer',
    cellRendererParams: (row: ValueFormatterParams) => ({
      id: row.data.fileId,
      fileNumber: row.data.fileNumber,
      accountNumber: row.data.accountNumber,
    }),
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListFileNumberSort'),
    },
  },
  {
    field: 'address',
    headerName: 'Property Address',
    tooltipField: 'address',
    width: 300,
    minWidth: 300,
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListPropertyAddressSort'),
    },
  },
  {
    field: 'buyer',
    headerName: 'Buyer',
    tooltipField: 'buyer',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
    filterParams: { qaAttribute: 'FileListBuyer', hideFilterButton: true },
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListBuyerSort'),
    },
  },
  {
    field: 'office',
    headerName: 'Office',
    tooltipField: 'office',
    minWidth: 370,
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListOfficeSort'),
    },
  },
  {
    field: 'transactionType',
    headerName: 'Transaction Type',
    tooltipField: 'transactionType',
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListTransactionTypeSort'),
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      values: (params: { success: (v: (string | null)[]) => void }) =>
        params.success([
          'Sale/Cash',
          'Sale w/Mortgage',
          'Refinance',
          'Equity Loan',
          'Sale w/Construction Loan',
          'Other',
          null,
        ]),
      suppressAndOrCondition: true,
    },
  },
  {
    field: 'openDate',
    headerName: 'Created Date',
    cellRenderer: 'createDateRenderer',
    sort: 'desc',
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListCreatedDateSort'),
    },
    tooltipValueGetter: dateTooltipValue,
    filter: 'agDateColumnFilter',
    filterParams: {
      type: 'date',
      qaAttribute: 'FileListCreatedDate',
      comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
        const cellDate = getStartOfDay(cellValue);
        if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) return 0;
        if (cellDate.getTime() < filterLocalDateAtMidnight.getTime()) return -1;
        if (cellDate.getTime() > filterLocalDateAtMidnight.getTime()) return 1;
        return 0;
      },
      browserDatePicker: true,
      dateFormat: 'yyyy-mm-dd',
      inRangeInclusive: true,
    },
  },
  {
    field: 'pdf',
    headerName: '',
    cellClass: 'center-align-column jacket-pdf-cell',
    cellRenderer: 'summaryRenderer',
    cellRendererParams: (row: ValueFormatterParams) => ({
      id: row.data.fileId,
      fileNumber: row.data.fileNumber,
    }),
    width: 40,
    minWidth: 40,
    filter: false,
    floatingFilter: false,
    pinned: 'right',
    headerComponentParams: {
      templateFunction: () => buildHeaderTemplate('FileListSummarySort'),
    },
  },
];
